import { useEffect } from 'react';
import {
  type DefaultValues,
  type FieldValues,
  type UseFormReturn,
  useForm,
} from 'react-hook-form';

export interface UseFilterDrawerOptions<T extends FieldValues> {
  open: boolean;
  filters: T;
  emptyFilters: T;
  onApply: (filters: T) => void;
  onClear: () => void;
}

export interface UseFilterDrawerReturn<T extends FieldValues> {
  methods: UseFormReturn<T>;
  handleApply: () => void;
  handleClear: () => void;
}

export function useFilterDrawer<T extends FieldValues>({
  open,
  filters,
  emptyFilters,
  onApply,
  onClear,
}: UseFilterDrawerOptions<T>): UseFilterDrawerReturn<T> {
  const methods = useForm<T>({
    defaultValues: emptyFilters as DefaultValues<T>,
  });
  const { reset, handleSubmit } = methods;

  useEffect(() => {
    if (open) {
      reset(filters);
    }
  }, [open, reset, filters]);

  const handleClear = () => {
    reset(emptyFilters);
    onClear();
  };

  return {
    methods,
    handleApply: handleSubmit(onApply),
    handleClear,
  };
}
